// Resolves the signed-in user's revenue role from the Keycloak access token.
//
// `keycloak.tokenParsed` is the decoded access-token payload, so no JWT
// decoding is needed here — this module is a pure function over that object so
// it can be unit-tested without a Keycloak instance.

/**
 * The role value sent to analytics. Carries the Keycloak role name verbatim —
 * there is deliberately no abbreviation layer, so a value seen in PostHog
 * traces straight back to a realm role with no mapping table to drift.
 *
 * `unknown` is lower-case on purpose: it is a sentinel for "no revenue role on
 * the token", not a Keycloak role, so it can never be mistaken for one.
 */
export type UserRole =
  | "REVENUE_ANALYST"
  | "SENIOR_REVENUE_ANALYST"
  | "REVENUE_MANAGER"
  | "unknown";

// Ordered most-senior first: the first match wins if a user carries several.
const REVENUE_ROLES = [
  "REVENUE_MANAGER",
  "SENIOR_REVENUE_ANALYST",
  "REVENUE_ANALYST",
] as const satisfies readonly UserRole[];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

/** Reads `.roles` off a `realm_access`/`resource_access[client]` container. */
const rolesOf = (container: unknown): string[] => {
  if (!container || typeof container !== "object") return [];
  const roles = (container as { roles?: unknown }).roles;
  return isStringArray(roles) ? roles : [];
};

/**
 * Collects realm roles and every client's roles from a parsed access token.
 * Both claims are read because whether a role is granted at the realm or the
 * client level is a Keycloak configuration detail this app shouldn't depend on.
 */
const collectTokenRoles = (tokenParsed: unknown): string[] => {
  if (!tokenParsed || typeof tokenParsed !== "object") return [];
  const token = tokenParsed as {
    realm_access?: unknown;
    resource_access?: unknown;
  };

  const clientRoles =
    token.resource_access && typeof token.resource_access === "object"
      ? Object.values(token.resource_access as Record<string, unknown>).flatMap(
          rolesOf
        )
      : [];

  return [...rolesOf(token.realm_access), ...clientRoles];
};

/**
 * Resolves the token's revenue role, or `"unknown"` when it carries none of
 * them. Only the three revenue roles are recognised — a token with other realm
 * roles (admin, ops) resolves to `"unknown"` rather than emitting an
 * open-ended set of values into analytics.
 */
export const resolveUserRole = (tokenParsed: unknown): UserRole => {
  const roles = new Set(collectTokenRoles(tokenParsed));
  return REVENUE_ROLES.find((role) => roles.has(role)) ?? "unknown";
};
